import { Dispatch, SetStateAction } from 'react';
import { X } from 'react-feather';

import {
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Label,
  LoadingSpinner,
  ScrollArea,
  Textarea,
} from '@shared/ui';

import { supportWidgetLanguage } from '../model';
import { useFeedback } from '../model/hook/useFeedBack';

type TFeedbackModalProps = {
  setIsWidgetOpen: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export const Feedback = ({ setIsWidgetOpen, isOpen, setIsOpen }: TFeedbackModalProps) => {
  const { handleRankSelect, handleSubmit, watchedRank, rankButtons, form, isLoading } = useFeedback(
    {
      setIsOpen,
      setIsWidgetOpen,
    }
  );
  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className='w-full max-h-[100svh]'>
        <DrawerHeader>
          <div className='flex items-center justify-between'>
            <DrawerTitle className='body-13-bold'>
              {supportWidgetLanguage.submitFeedback}
            </DrawerTitle>
            <DrawerClose className='focus-visible:outline-none'>
              <X size={24} strokeWidth={1} />
            </DrawerClose>
          </div>{' '}
        </DrawerHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className='flex flex-col justify-between w-full pt-5 md:p-2 h-[calc(100svh-105px)] md:h-auto'
          >
            <ScrollArea className='h-full md:h-[350px]' dir='rtl'>
              <div className='flex flex-col gap-y-3 md:p-3 md:pb-0'>
                <div className='flex flex-col items-center gap-4'>
                  <div className='text-center'>
                    <h3 className='body-12-regular md:body-14-medium text-text mb-2'>
                      امتیاز شما به سامانه آگاه من
                    </h3>
                    <div className='w-16 h-16 mx-auto mb-3 flex items-center justify-center'>
                      {watchedRank &&
                        (() => {
                          const selectedButton = rankButtons.find(
                            (button) => button.rank === watchedRank
                          );
                          return selectedButton ? (
                            <Label className='text-5xl leading-none'>{selectedButton.emoji}</Label>
                          ) : null;
                        })()}
                    </div>
                    <Label className='body-12-regular'>
                      {watchedRank === 1 && 'خیلی ضعیف'}
                      {watchedRank === 2 && 'ضعیف'}
                      {watchedRank === 3 && 'متوسط'}
                      {watchedRank === 4 && 'خوب'}
                      {watchedRank === 5 && 'عالی'}
                    </Label>
                  </div>

                  <FormField
                    name='rank'
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className='w-full'>
                        <FormControl>
                          <div className='flex items-center justify-between md:justify-center md:gap-5'>
                            {rankButtons.map(({ rank }) => (
                              <Button
                                key={rank}
                                type='button'
                                className={`w-[60px] h-[60px] md:size-8 p-0 rounded-lg h4-bold md:body-14-bold leading-[150%] ${
                                  watchedRank === rank
                                    ? 'bg-pics hover:bg-pics'
                                    : 'bg-hover hover:bg-border border-none text-text active:text-white'
                                }`}
                                onClick={() => handleRankSelect(rank)}
                                disabled={isLoading}
                              >
                                {rank}
                              </Button>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage className='body-12-regular text-red-1 text-center !mt-2' />
                      </FormItem>
                    )}
                  />

                  <Label className='body-12-regular text-center'>
                    نظرات و پیشنهادات خود را برای ما ارسال کنید
                  </Label>
                </div>

                <FormField
                  name='description'
                  control={form.control}
                  render={({ field }) => (
                    <FormItem className='!w-full px-1 py-1'>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder='توضیحات'
                          disabled={isLoading}
                          rows={10}
                          className='w-full min-h-[218px] md:min-h-[100px] md:max-h-[100px] resize-none body-12-regular'
                          maxLength={3000}
                        />
                      </FormControl>
                      <FormMessage className='body-12-regular text-red-1 !mt-2' />
                    </FormItem>
                  )}
                />
              </div>
            </ScrollArea>

            <div className='flex justify-center items-center w-full md:w-auto gap-3 pt-[16px] md:pb-[8px] md:mt-2 md:border-t md:border-border border-background md:-mx-[8px]'>
              <Button
                type='submit'
                disabled={isLoading}
                className='md:px-[32px] md:py-[8px] h-[60px] md:h-auto w-full md:w-auto items-center gap-2 md:mx-0'
              >
                {isLoading && <LoadingSpinner className='text-pics scale-[0.6]' />}
                ثبت
              </Button>
            </div>
          </form>
        </Form>{' '}
      </DrawerContent>
    </Drawer>
  );
};
